// Wire model for GET /mapTilingService/prices -> { prices: [...] }.
//
// Plain interfaces + builder functions rather than the `class` + `static build()` style used by
// PriceModel: these values end up inside a mapbox-gl geojson source and come back out through
// `ev.features[0].properties`, and getPriceSource() spreads them into an object literal - both of
// which drop class methods. They have to stay plain data.
//
// Kept import-free on purpose so scripts/generate-price-pages.ts can import it under tsx.

export interface MillPriceTier {
    name: string
    delta: number
    price: number
    cardinality: number
    price_pempem: number
    // Not sent by the API - derived here, true on the best (highest cardinality) tier.
    price_highlighted: boolean
}

export interface MillPriceOnMap {
    id: number
    mill_id: number
    mill_name?: string
    mill_alias?: string
    mill_full_name?: string
    group_id?: number
    off_platform: boolean

    coordinates?: number[]
    location_village?: string
    location_subdistrict?: string

    market_date?: string
    price_type?: string
    program_name?: string

    price_per_kg?: number
    program_delta_bonus?: number
    tiers: MillPriceTier[]

    minimum_quality_level?: string
    sorting_strictness?: string
    minimum_bjr?: number
    standard_grading_ratio?: number
    require_spb?: string
    require_spsi: boolean
    accepts_small_truck: boolean
    accepts_dropoff: boolean
    is_brondolan: boolean
    unloading_fee_pre_applied: boolean
    bid_warning_message?: string

    mill_officer_name?: string
    mill_officer_phonenumber?: string
}

export function buildMillPrice(p: any): MillPriceOnMap {
    return {
        id: p.id,
        mill_id: p.mill_id,
        mill_name: p.mill_name,
        mill_alias: p.mill_alias,
        mill_full_name: p.mill_full_name,
        group_id: p.group_id,
        off_platform: p.off_platform ?? false,

        coordinates: parseCoordinates(p.position),
        location_village: p.location_village,
        location_subdistrict: p.location_subdistrict,

        market_date: p.market_date,
        price_type: p.price_type,
        program_name: p.program_name,

        price_per_kg: p.pricing?.price_per_kg,
        program_delta_bonus: p.pricing?.program_delta_bonus,
        tiers: buildTiers(p.pricing?.tiers),

        minimum_quality_level: p.minimum_quality_level,
        sorting_strictness: p.sorting_strictness,
        minimum_bjr: p.minimum_bjr,
        standard_grading_ratio: p.standard_grading_ratio,
        require_spb: p.require_spb,
        require_spsi: p.require_spsi ?? false,
        accepts_small_truck: p.accepts_small_truck ?? false,
        accepts_dropoff: p.accepts_dropoff ?? false,
        is_brondolan: p.is_brondolan ?? false,
        unloading_fee_pre_applied: p.unloading_fee_pre_applied ?? false,
        bid_warning_message: p.bid_warning_message,

        mill_officer_name: p.mill_officer_name,
        mill_officer_phonenumber: p.mill_officer_phonenumber,
    }
}

// Off-platform mills never get advertised, and a price with no position cannot be placed on the map.
export function buildMillPrices(prices: any[]): MillPriceOnMap[] {
    return prices?.map(p => buildMillPrice(p)).filter(p => p.coordinates && !p.off_platform) ?? []
}

// The number advertised on the map bubble: the best QBP tier's total, i.e. the same tier the popup
// highlights in blue - not the flat `price_per_kg`, which is only the base before quality bonuses.
// Mills that publish no tiers still fall back to the flat price.
export function millPriceHeadline(properties: any): number | undefined {
    const tiers = parseTiers(properties?.tiers)
    const best = tiers.find(t => t.price_highlighted) ?? tiers.at(0)
    return best?.price_pempem ?? properties?.price_per_kg
}

// mapbox can hand nested arrays back from a source as JSON strings, so tolerate both.
function parseTiers(tiers: any): MillPriceTier[] {
    try {
        const parsed = typeof tiers === "string" ? JSON.parse(tiers) : tiers
        return Array.isArray(parsed) ? parsed : []
    } catch (e) {
        console.error("Error parsing price.tiers:", e)
        return []
    }
}

// Best tier first, so price_highlighted and the popup colour ramp always agree.
function buildTiers(tiers: any[]): MillPriceTier[] {
    return (tiers?.map((t: any) => ({
        name: t.name,
        delta: t.delta,
        price: t.price,
        cardinality: t.cardinality,
        price_pempem: t.price_pempem,
        price_highlighted: false,
    })) ?? [])
        .sort((a, b) => (b.cardinality ?? 0) - (a.cardinality ?? 0))
        .map((t, i) => ({ ...t, price_highlighted: i === 0 }))
}

// `position` arrives as a stringified GeoJSON Point - one malformed row must not lose the payload.
function parseCoordinates(position: any): number[] | undefined {
    if (!position) {
        return undefined
    }
    try {
        const parsed = typeof position === "string" ? JSON.parse(position) : position
        return Array.isArray(parsed?.coordinates) ? parsed.coordinates : undefined
    } catch (e) {
        console.error("Error parsing price.position:", e)
        return undefined
    }
}

export function toPriceFeatureCollection(prices: MillPriceOnMap[]): any {
    return {
        'type': 'FeatureCollection',
        'features': prices.map(price => ({
            "type": 'Feature',
            "geometry": {
                'type': 'Point',
                'coordinates': price.coordinates
            },
            'properties': { ...price }
        }))
    }
}
